import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, ArrowRight, Phone, Globe, Shield, Bot, DollarSign, Users, Search, Zap, MapPin } from 'lucide-react';
import SiteFooter from './SiteFooter';
import SEOHead from './SEOHead';

const FEATURES = [
  { name: 'Call Tracking (CallRail)', us: true, them: false, category: 'tracking' },
  { name: 'Dynamic Number Insertion (DPNI)', us: true, them: false, category: 'tracking' },
  { name: 'Call Recording & Attribution', us: true, them: false, category: 'tracking' },
  { name: 'Listings Sync (200+ Directories)', us: true, them: true, category: 'listings' },
  { name: 'Citation Builder / Tracker', us: true, them: true, category: 'citations' },
  { name: 'NAP Consistency Audits', us: true, them: true, category: 'citations' },
  { name: 'AI-Powered Search Insights (Scout)', us: false, them: true, category: 'ai' },
  { name: 'Location Pages (Schema + SEO)', us: true, them: true, category: 'seo' },
  { name: '3,750+ Programmatic SEO Pages', us: true, them: false, category: 'seo' },
  { name: 'AI-Powered Blog (Daily Auto-Posts)', us: true, them: false, category: 'content' },
  { name: 'Schema Stacking (7 Types)', us: true, them: false, category: 'seo' },
  { name: 'IndexNow + Google Indexing API', us: true, them: false, category: 'seo' },
  { name: 'Review Monitoring', us: true, them: true, category: 'reputation' },
  { name: 'Review Generation', us: true, them: true, category: 'reputation' },
  { name: 'White-Label Client Dashboard', us: true, them: false, category: 'agency' },
  { name: 'Client Impersonation (Admin View)', us: true, them: false, category: 'agency' },
  { name: 'Subscription Dunning System', us: true, them: false, category: 'billing' },
  { name: 'Failed Payment Auto-Recovery', us: true, them: false, category: 'billing' },
  { name: 'SMS Campaigns ($0.049/msg)', us: true, them: false, category: 'marketing' },
  { name: 'Email Marketing', us: true, them: false, category: 'marketing' },
  { name: 'Stripe/PayPal Billing', us: true, them: false, category: 'billing' },
  { name: 'Voice & AI Search Optimization', us: false, them: true, category: 'ai' },
  { name: 'Competitor Tracking', us: false, them: true, category: 'seo' },
  { name: 'Real-Time Lead Attribution', us: true, them: false, category: 'tracking' },
];

const PRICING = [
  { label: 'Yext Emerging', price: '$199/mo', features: 'Basic listings sync, single location' },
  { label: 'Yext Essential', price: '$449/mo', features: 'Listings + reviews, more directories' },
  { label: 'Yext Complete', price: '$499/mo', features: 'Full platform, pages, analytics' },
  { label: 'Yext Premium', price: '$999/mo', features: 'AI Scout, advanced analytics, priority support' },
];

const COLORS = { primary: '#002FA7', dark: '#0a0f1e', accent: '#FFCC00' };

export default function ComparisonYext() {
  const { t } = useTranslation('compare');
  const usWins = FEATURES.filter(f => f.us && !f.them).length;
  const theyWin = FEATURES.filter(f => !f.us && f.them).length;
  const both = FEATURES.filter(f => f.us && f.them).length;

  return (
    <>
      <SEOHead
        title="LocalBranding Pro vs Yext — Save $199-999/mo on Local Marketing 2026"
        description="Compare LocalBranding Pro and Yext. Yext charges $199-999/mo just for listings. We include call tracking, AI blog, citations, dunning, and a client dashboard — all in one."
        canonical="https://localbrandingpro.com/compare/yext"
        keywords="LocalBranding Pro vs Yext, Yext alternative, Yext competitor, local listings management, local SEO platform comparison"
      />
      <div style={{ fontFamily: "'Outfit', 'IBM Plex Sans', sans-serif" }}>
        <section className="relative overflow-hidden" style={{ background: COLORS.dark }}>
          <div className="absolute inset-0 opacity-10" style={{ background: `radial-gradient(circle at 50% 50%, #0ea5e9, transparent 55%)` }} />
          <div className="max-w-6xl mx-auto px-6 py-20 relative z-10 text-center">
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] mb-4 px-4 py-1.5 rounded-full" style={{ background: `${COLORS.accent}20`, color: COLORS.accent }}>
              Listings Platform Comparison 2026
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-testid="comparison-title">
              LocalBranding Pro
              <span className="block text-2xl sm:text-3xl font-normal text-slate-400 mt-2">{t('yext.titleSub')}</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-8">
              Yext pioneered listings sync — but at $199-999/mo, you're paying premium prices for one thing. We give you listings, call tracking, AI content, billing, and a full client dashboard.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold" style={{ color: COLORS.accent }}>{usWins}</p>
                <p className="text-xs text-slate-400 mt-1">Features Only We Have</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-white">{both}</p>
                <p className="text-xs text-slate-400 mt-1">Shared Features</p>
              </div>
              <div className="text-center px-6 py-3 rounded-xl bg-white/5 border border-white/10">
                <p className="text-3xl font-bold text-slate-500">{theyWin}</p>
                <p className="text-xs text-slate-400 mt-1">Yext Only</p>
              </div>
            </div>
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-lg text-white font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.primary }} data-testid="cta-hero">
              Try LocalBranding Pro Free <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </section>

        <section className="py-16 bg-white" data-testid="comparison-table-section">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-2" style={{ color: COLORS.dark }}>Side-by-side feature comparison</h2>
            <p className="text-center text-slate-500 text-sm mb-10">Yext does listings well — we do everything else too</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead><tr className="bg-slate-50">
                  <th className="text-left px-6 py-4 font-semibold text-slate-700">Feature</th>
                  <th className="text-center px-6 py-4 font-bold" style={{ color: COLORS.primary }}>LocalBranding Pro</th>
                  <th className="text-center px-6 py-4 font-semibold text-slate-500">Yext</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {FEATURES.map((f, i) => (
                    <tr key={i} className={`${f.us && !f.them ? 'bg-blue-50/30' : ''} hover:bg-slate-50`}>
                      <td className="px-6 py-3.5 text-slate-700">{f.name}</td>
                      <td className="px-6 py-3.5 text-center">{f.us ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                      <td className="px-6 py-3.5 text-center">{f.them ? <CheckCircle2 className="h-5 w-5 inline text-emerald-500" /> : <XCircle className="h-5 w-5 inline text-slate-300" />}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="py-16" style={{ background: '#f8fafc' }}>
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>Why agencies choose us over Yext</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: DollarSign, title: 'Fraction of the Cost', desc: 'Yext starts at $199/mo per location for basic listings. We include listings, call tracking, AI blog, and a full dashboard for less.' },
                { icon: Phone, title: 'Call Tracking + Attribution', desc: 'Yext can\'t tell you which listing drove a phone call. We show caller name, source, recording, and conversion data.' },
                { icon: Bot, title: 'AI Visibility Scanning', desc: 'Track how your business appears in AI search engines. Yext has AI Scout for insights — we track your AI discoverability.' },
                { icon: Shield, title: 'Revenue Protection', desc: '4-stage dunning system with automated recovery emails. Yext is a tool, not a billing platform.' },
                { icon: MapPin, title: '3,750+ SEO Pages', desc: 'Programmatic city + industry pages with schema stacking. Yext has Pages, but not at this scale or with this depth.' },
                { icon: Users, title: 'Full Agency Platform', desc: 'Client dashboard, impersonation, SMS, email, billing — everything you need to run an agency. Not just listings.' },
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-slate-200 hover:shadow-md transition-shadow">
                  <item.icon className="h-8 w-8 mb-4" style={{ color: COLORS.primary }} />
                  <h3 className="font-bold text-lg mb-2" style={{ color: COLORS.dark }}>{item.title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10" style={{ color: COLORS.dark }}>{t('yext.pricingTitle')}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              {PRICING.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-5 text-center">
                  <p className="text-sm font-semibold text-slate-500 mb-2">{p.label}</p>
                  <p className="text-2xl font-bold mb-1" style={{ color: COLORS.dark }}>{p.price}</p>
                  <p className="text-xs text-slate-400">{p.features}</p>
                  <p className="text-xs text-red-400 mt-2">per location</p>
                </div>
              ))}
            </div>
            <div className="rounded-xl p-8 text-center" style={{ background: COLORS.primary }}>
              <p className="text-white/80 text-sm mb-2">LocalBranding Pro</p>
              <p className="text-4xl font-bold text-white mb-2">Everything Yext does. Plus everything it doesn't.</p>
              <p className="text-white/60 text-sm mb-6">Call tracking + Listings + AI Blog + Dunning + Client Dashboard — one platform, one price</p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-3 rounded-lg font-bold transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }} data-testid="cta-pricing">
                Get Started Free <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16" style={{ background: COLORS.dark }}>
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Stop overpaying for just listings</h2>
            <p className="text-slate-400 mb-8">Switch from Yext and get 10x more features at a fraction of the cost. We'll migrate your listings in 24 hours.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="px-8 py-4 rounded-lg font-bold text-lg transition-all hover:scale-105" style={{ background: COLORS.accent, color: COLORS.dark }}>Start Free Trial</Link>
              <Link to="/compare/gohighlevel" className="px-8 py-4 rounded-lg font-bold text-lg border border-white/20 text-white hover:bg-white/10">vs GoHighLevel</Link>
            </div>
          </div>
        </section>
        <SiteFooter />
      </div>
    </>
  );
}
